import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { service } from '../lib/api/service'
import type { Project, APris } from '../lib/api/types'
import { DEFAULT_GROUPS, withStandardGroups, type Group } from '../lib/aprisGroups'

export default function APrisListaPrint() {
  const navigate = useNavigate()
  const [items, setItems]     = useState<APris[]>([])
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([service.getAPriser(), service.getProject()])
      .then(([a, p]) => { setItems(a); setProject(p) })
      .finally(() => setLoading(false))
  }, [])

  const doPrint = () => {
    document.body.dataset.printing = '1'
    window.print()
    delete document.body.dataset.printing
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const proj: Partial<Project> = project ?? {}
  const today  = new Date().toISOString().slice(0, 10)
  const groups: Group[] = withStandardGroups(proj.aprisGrupper ? (JSON.parse(proj.aprisGrupper) as Group[]) : DEFAULT_GROUPS)

  const groupedItems = groups.map(g => ({
    ...g,
    rows: items.filter(i => (i.kod ?? '').startsWith(g.prefix + '-')),
  })).filter(g => g.rows.length > 0)

  const ungrouped = items.filter(i => !groups.some(g => (i.kod ?? '').startsWith(g.prefix + '-')))

  return (
    <div className="print-overlay">
      <div className="print-toolbar no-print">
        <button className="icon-btn" onClick={() => navigate('/prislista')}>
          <ArrowLeft size={14} /> Tillbaka
        </button>
        <span style={{ fontSize: 13, color: 'var(--ink-dim)' }}>
          À-prislista · {items.length} rader
        </span>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={doPrint}>
          <Printer size={14} /> Skriv ut
        </button>
      </div>

      <div className="paper">
        <div className="paper-letterhead">
          <div>
            <div className="paper-sender-name">TIBRAM BYGG AB</div>
            <div className="paper-sender-addr">{proj.adress ?? ''}</div>
          </div>
          <div>
            <div className="paper-doc-type">À-prislista</div>
            <div className="paper-doc-ref">{proj.projektnr ?? '–'}</div>
          </div>
        </div>

        <div style={{ fontSize: '9pt', color: '#666', margin: '8pt 0 14pt' }}>
          {proj.projektNamn && <>{proj.projektNamn}{proj.ort ? ', ' + proj.ort : ''} &nbsp;·&nbsp; </>}
          Utskriven: {today}
        </div>

        {groupedItems.map(g => (
          <div key={g.prefix} style={{ marginBottom: '16pt' }}>
            <div style={{ fontSize: '9pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#555', borderBottom: '.5pt solid #ccc', paddingBottom: '4pt', marginBottom: '6pt' }}>
              {g.label} ({g.prefix}-)
            </div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '50pt' }}>Kod</th>
                  <th>Benämning</th>
                  <th style={{ width: '40pt' }}>Enhet</th>
                  <th className="r" style={{ width: '70pt' }}>À-pris</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map(item => (
                  <tr key={item.sortOrder}>
                    <td className="code">{item.kod ?? ''}</td>
                    <td>{item.benamning || '–'}</td>
                    <td>{item.enhet || '–'}</td>
                    <td className="r">{item.pris != null ? item.pris.toLocaleString('sv-SE') + ' kr' : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {ungrouped.length > 0 && (
          <div>
            <div style={{ fontSize: '9pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#555', borderBottom: '.5pt solid #ccc', paddingBottom: '4pt', marginBottom: '6pt' }}>
              Övriga
            </div>
            <table>
              <thead>
                <tr>
                  <th>Benämning</th>
                  <th style={{ width: '40pt' }}>Enhet</th>
                  <th className="r" style={{ width: '70pt' }}>À-pris</th>
                </tr>
              </thead>
              <tbody>
                {ungrouped.map(item => (
                  <tr key={item.sortOrder}>
                    <td>{item.benamning || '–'}</td>
                    <td>{item.enhet || '–'}</td>
                    <td className="r">{item.pris != null ? item.pris.toLocaleString('sv-SE') + ' kr' : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="paper-footer">
          {proj.projektnr ?? 'À-prislista'} &nbsp;—&nbsp; Utskriven {today}
        </div>
      </div>
    </div>
  )
}
